/**
 * WRMS Request ID Generator — Digi Sahyadri
 * Generates unique sequential IDs: WRMS-LEAVE-2026-00001
 */

import { db } from '../../config/firebase';
import { doc, runTransaction, getDoc } from 'firebase/firestore';

const TYPE_CODES = {
  leave: 'LV',
  early_leave: 'EL',
  gate_pass: 'GP',
  extra_work: 'EW',
};

/**
 * Generate a unique Request ID using a Firestore counter document.
 * Uses a transaction to guarantee uniqueness even with concurrent submissions.
 * @param {'leave'|'early_leave'|'gate_pass'|'extra_work'} requestType
 * @returns {Promise<string>} e.g. "WRMS-LV-2026-00042"
 */
export const generateRequestId = async (requestType) => {
  const year = new Date().getFullYear();
  const typeCode = TYPE_CODES[requestType] || 'RQ';
  const counterRef = doc(db, 'wrms_counters', `${typeCode}_${year}`);

  try {
    const newCount = await runTransaction(db, async (transaction) => {
      const counterDoc = await transaction.get(counterRef);
      const currentCount = counterDoc.exists() ? counterDoc.data().count : 0;
      const nextCount = currentCount + 1;
      transaction.set(counterRef, { count: nextCount, year, typeCode });
      return nextCount;
    });

    const paddedCount = String(newCount).padStart(5, '0');
    return `WRMS-${typeCode}-${year}-${paddedCount}`;
  } catch (error) {
    console.error('[RequestIDGenerator] Error generating ID:', error);
    // Fallback: timestamp-based ID (non-sequential but unique)
    const ts = Date.now().toString().slice(-6);
    return `WRMS-${typeCode}-${year}-T${ts}`;
  }
};
